import { parseQuantity, parseUnitPrice, } from "./entities.js";
import { resolvePrice, } from "./resolution.js";
/**
 * Pricing at the till, in the browser.
 *
 * This file contains no pricing rules. It converts the wire snapshot
 * `/sales/pos/lookup` returned into the shapes `resolvePrice` takes, and calls
 * it. The precedence — status, validity window, most specific tier, and "when
 * nothing applies, say so" — lives in `@aeris/pricing` and runs here unchanged,
 * which is the entire reason that package depends on nothing but `@aeris/money`.
 *
 * Why do it here at all: a till that asked the server again every time the
 * operator pressed `+` would put a network round trip between a keystroke and
 * the number on the screen, on the one screen in the product where that is
 * felt. The tiers arrived with the scan; the answer is already computable.
 *
 * What this deliberately is NOT is a cache of prices across sales. The snapshot
 * belongs to the line it was captured for and dies with it — a till holding
 * yesterday's tiers would sell at yesterday's prices, and that is a bug nobody
 * would find until a customer complained.
 */
const toContext = (snapshot) => snapshot.priceList === null
    ? null
    : {
        id: snapshot.priceList.id,
        code: snapshot.priceList.code,
        name: snapshot.priceList.name,
        currency: snapshot.priceList.currency,
        isDefault: snapshot.priceList.isDefault,
        validFrom: snapshot.priceList.validFrom === null
            ? null
            : new Date(snapshot.priceList.validFrom),
        validTo: snapshot.priceList.validTo === null
            ? null
            : new Date(snapshot.priceList.validTo),
        status: snapshot.priceList.status,
    };
/**
 * A wire tier as a `PriceListItem`.
 *
 * Four fields are filled with placeholders because `resolvePrice` reads none of
 * them: it uses the id, the two amounts, the window and the status, and nothing
 * else. Sending `tenantId` to a browser would be sending an identifier the
 * browser has no use for, and inventing a narrower type here would be a second
 * definition of a stored price.
 */
const toItems = (snapshot) => snapshot.tiers.map((tier) => ({
    id: tier.id,
    tenantId: '',
    priceListId: snapshot.priceList?.id ?? '',
    variantId: tier.variantId,
    unitPrice: parseUnitPrice(tier.unitPrice),
    minQuantity: parseQuantity(tier.minQuantity),
    validFrom: tier.validFrom === null ? null : new Date(tier.validFrom),
    validTo: tier.validTo === null ? null : new Date(tier.validTo),
    status: tier.status,
    createdAt: new Date(0),
    updatedAt: new Date(0),
    version: 0,
}));
/**
 * What this variant costs at this quantity, right now.
 *
 * `at` is passed in rather than defaulted to `new Date()` inside, so a caller
 * pricing several lines of one cart prices them all at the same instant. A
 * promotion that ends between line three and line four would otherwise produce
 * a cart nobody can explain.
 */
export const priceAt = (snapshot, quantity, at = new Date()) => resolvePrice({
    list: toContext(snapshot),
    items: toItems(snapshot),
    variantId: snapshot.variantId,
    fallbackVariantId: snapshot.fallbackVariantId,
    quantity,
    at,
});
//# sourceMappingURL=snapshot.js.map